import type { AuditPage, AuditQuery, Tally } from './api'
import { NO_BAR, auditQueryFrom, createFilterBar, type AuditChoices, type Clock } from './auditFilter'
import { createTrail, emptyTrail, type TrailState } from './trail'

// The Audit trail screen with no component in it: what the bar holds, what the
// trail was read with, the trail itself and the acts the chain holds.
//
// # WHY IT IS ONE THING
//
// The bar and the trail used to be joined in the component: an effect started
// a read when the filter changed, and a line of the component decided which
// filter that was. Each was a place for the two to come apart with nothing to
// notice, and the second review's mutants found both (11 October 2026): the
// trail read with what was half typed while the chips showed what had been
// read before, and a bar whose typing went nowhere. Joined here, a test types
// into it, moves the clock and sees exactly which reads were asked for; and
// the component, handed one of these by a test, is drawn in whatever state
// the test has put it in.

export type AuditScreenState = {
  choices: AuditChoices
  applied: AuditChoices
  trail: TrailState
  // null until the vocabulary read answers, and null is not the empty list. A
  // tenant that has recorded nothing and a read that never came back are
  // opposite facts about the same chain, and one empty row for both tells an
  // auditor the trail is empty when it is only unread.
  actions: Tally[] | null
  actionsError: unknown
}

export type AuditScreen = {
  state: () => AuditScreenState
  subscribe: (listener: () => void) => () => void
  // A tick, a removed chip, Clear, Enter: read now.
  choose: (next: AuditChoices) => void
  // A typed box: read when the typing pauses.
  type: (next: AuditChoices) => void
  further: () => void
  readActs: () => void
  // The screen is on the page, and off it again.
  open: () => void
  close: () => void
}

// The filter a set of choices stands for, as the key the trail is read under.
export function trailKey(c: AuditChoices): string {
  return JSON.stringify(auditQueryFrom(c))
}

export function createAuditScreen(
  api: { read: (q: AuditQuery) => Promise<AuditPage>; acts: () => Promise<Tally[]> },
  pauseMs: number,
  clock?: Clock,
): AuditScreen {
  let bar = NO_BAR
  let actions: Tally[] | null = null
  let actionsError: unknown = null
  let on = false
  let reading = ''
  let actsAsked = 0
  const listeners = new Set<() => void>()
  const trail = createTrail(api.read)
  let state: AuditScreenState = { ...bar, trail: emptyTrail(trailKey(bar.applied)), actions, actionsError }
  const tell = () => {
    state = { ...bar, trail: trail.state(), actions, actionsError }
    for (const told of listeners) told()
  }
  trail.subscribe(tell)

  const filter = createFilterBar((next) => {
    bar = next
    // The trail is read with what was applied, and never with what the boxes
    // hold while somebody is still typing. Started here, in the same step as
    // the change, so nobody is told of new chips over the last filter's rows.
    const key = trailKey(next.applied)
    if (on && key !== reading) {
      reading = key
      trail.start(key)
    } else tell()
  }, pauseMs, clock)

  const readActs = () => {
    const mine = ++actsAsked
    actions = null
    actionsError = null
    tell()
    api.acts().then(
      (list) => {
        if (mine !== actsAsked) return
        actions = list
        tell()
      },
      // The screen works without the vocabulary; it costs the list of acts and
      // nothing else, and the boxes still filter. What it must not do is
      // swallow the failure, which is what drew a failed read as a tenant with
      // an empty chain: the Acts control says it was not read.
      (error) => {
        if (mine !== actsAsked) return
        actionsError = error
        tell()
      },
    )
  }

  return {
    state: () => state,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    choose: filter.choose,
    type: filter.type,
    further: trail.further,
    readActs,
    open() {
      on = true
      reading = trailKey(bar.applied)
      trail.start(reading)
      readActs()
    },
    close() {
      on = false
      filter.stop()
      trail.stop()
      actsAsked++
    },
  }
}
